import React, { useMemo, useRef, useState } from 'react';

// A full, categorized emoji pack (the common Unicode set people actually use),
// plus a reusable <EmojiPicker> — category tabs, search, and a recents row.
// Shared by the composer and the message reaction picker.

export type EmojiCategory = { key: string; label: string; icon: string; emojis: string[] };

const split = (s: string) => s.trim().split(/\s+/);

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    key: 'smileys', label: 'Smileys & people', icon: '😀', emojis: split(`
    😀 😃 😄 😁 😆 😅 🤣 😂 🙂 🙃 🫠 😉 😊 😇 🥰 😍 🤩 😘 😗 😚 😙 🥲 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🫢 🫣 🤫 🤔 🫡 🤐 🤨 😐 😑 😶 🫥 😶‍🌫️ 😏 😒 🙄 😬 😮‍💨 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🤧 🥵 🥶 🥴 😵 😵‍💫 🤯 🤠 🥳 🥸 😎 🤓 🧐 😕 🫤 😟 🙁 ☹️ 😮 😯 😲 😳 🥺 🥹 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠 🤬 😈 👿 💀 ☠️ 💩 🤡 👹 👺 👻 👽 👾 🤖 😺 😸 😹 😻 😼 😽 🙀 😿 😾
    🙈 🙉 🙊 💋 💌 💘 💝 💖 💗 💓 💞 💕 💟 ❣️ 💔 ❤️‍🔥 ❤️‍🩹 ❤️ 🧡 💛 💚 💙 💜 🤎 🖤 🤍 💯 💢 💥 💫 💦 💨 🕳️ 💬 👁️‍🗨️ 🗨️ 🗯️ 💭 💤
    👋 🤚 🖐️ ✋ 🖖 🫱 🫲 🫳 🫴 👌 🤌 🤏 ✌️ 🤞 🫰 🤟 🤘 🤙 👈 👉 👆 🖕 👇 ☝️ 🫵 👍 👎 ✊ 👊 🤛 🤜 👏 🙌 🫶 👐 🤲 🤝 🙏 ✍️ 💅 🤳 💪 🦾 🦿 🦵 🦶 👂 🦻 👃 🧠 🫀 🫁 🦷 🦴 👀 👁️ 👅 👄 🫦
    👶 🧒 👦 👧 🧑 👨 🧔 👩 🧓 👴 👵 🙍 🙎 🙅 🙆 💁 🙋 🧏 🙇 🤦 🤷 👮 🕵️ 💂 🥷 👷 🫅 🤴 👸 👳 👲 🧕 🤵 👰 🤰 🫃 🫄 🤱 👼 🎅 🤶 🦸 🦹 🧙 🧚 🧛 🧜 🧝 🧞 🧟 💆 💇 🚶 🧍 🧎 🏃 💃 🕺 🕴️ 👯 🧖 🧗 🤺 🏇 ⛷️ 🏂 🏌️ 🏄 🚣 🏊 ⛹️ 🏋️ 🚴 🚵 🤸 🤼 🤽 🤾 🤹 🧘 🛀 🛌 👭 👫 👬 💏 💑 👪 🗣️ 👤 👥 🫂
    `),
  },
  {
    key: 'nature', label: 'Animals & nature', icon: '🐻', emojis: split(`
    🐵 🐒 🦍 🦧 🐶 🐕 🦮 🐩 🐺 🦊 🦝 🐱 🐈 🐈‍⬛ 🦁 🐯 🐅 🐆 🐴 🐎 🦄 🦓 🦌 🦬 🐮 🐂 🐃 🐄 🐷 🐖 🐗 🐽 🐏 🐑 🐐 🐪 🐫 🦙 🦒 🐘 🦣 🦏 🦛 🐭 🐁 🐀 🐹 🐰 🐇 🐿️ 🦫 🦔 🦇 🐻 🐻‍❄️ 🐨 🐼 🦥 🦦 🦨 🦘 🦡 🐾 🦃 🐔 🐓 🐣 🐤 🐥 🐦 🐧 🕊️ 🦅 🦆 🦢 🦉 🦤 🪶 🦩 🦚 🦜 🐸 🐊 🐢 🦎 🐍 🐲 🐉 🦕 🦖 🐳 🐋 🐬 🦭 🐟 🐠 🐡 🦈 🐙 🐚 🪸 🐌 🦋 🐛 🐜 🐝 🪲 🐞 🦗 🪳 🕷️ 🕸️ 🦂 🦟 🪰 🪱 🦠 💐 🌸 💮 🪷 🏵️ 🌹 🥀 🌺 🌻 🌼 🌷 🌱 🪴 🌲 🌳 🌴 🌵 🌾 🌿 ☘️ 🍀 🍁 🍂 🍃 🪹 🪺 🍄 🌰 🦀 🦞 🦐 🦑 🌍 🌎 🌏 🌐 🌑 🌒 🌓 🌔 🌕 🌖 🌗 🌘 🌙 🌚 🌛 🌜 ☀️ 🌝 🌞 ⭐ 🌟 🌠 ☁️ ⛅ ⛈️ 🌤️ 🌥️ 🌦️ 🌧️ 🌨️ 🌩️ 🌪️ 🌫️ 🌬️ 🌈 ☂️ ☔ ⚡ ❄️ ☃️ ⛄ ☄️ 🔥 💧 🌊
    `),
  },
  {
    key: 'food', label: 'Food & drink', icon: '🍔', emojis: split(`
    🍇 🍈 🍉 🍊 🍋 🍌 🍍 🥭 🍎 🍏 🍐 🍑 🍒 🍓 🫐 🥝 🍅 🫒 🥥 🥑 🍆 🥔 🥕 🌽 🌶️ 🫑 🥒 🥬 🥦 🧄 🧅 🥜 🫘 🌰 🍞 🥐 🥖 🫓 🥨 🥯 🥞 🧇 🧀 🍖 🍗 🥩 🥓 🍔 🍟 🍕 🌭 🥪 🌮 🌯 🫔 🥙 🧆 🥚 🍳 🥘 🍲 🫕 🥣 🥗 🍿 🧈 🧂 🥫 🍱 🍘 🍙 🍚 🍛 🍜 🍝 🍠 🍢 🍣 🍤 🍥 🥮 🍡 🥟 🥠 🥡 🦪 🍦 🍧 🍨 🍩 🍪 🎂 🍰 🧁 🥧 🍫 🍬 🍭 🍮 🍯 🍼 🥛 ☕ 🫖 🍵 🍶 🍾 🍷 🍸 🍹 🍺 🍻 🥂 🥃 🫗 🥤 🧋 🧃 🧉 🧊 🥢 🍽️ 🍴 🥄
    `),
  },
  {
    key: 'travel', label: 'Travel & places', icon: '✈️', emojis: split(`
    🌐 🗺️ 🧭 🏔️ ⛰️ 🌋 🗻 🏕️ 🏖️ 🏜️ 🏝️ 🏞️ 🏟️ 🏛️ 🏗️ 🧱 🪨 🪵 🛖 🏘️ 🏚️ 🏠 🏡 🏢 🏣 🏤 🏥 🏦 🏨 🏩 🏪 🏫 🏬 🏭 🏯 🏰 💒 🗼 🗽 ⛪ 🕌 🛕 🕍 ⛩️ 🕋 ⛲ ⛺ 🌁 🌃 🏙️ 🌄 🌅 🌆 🌇 🌉 ♨️ 🎠 🛝 🎡 🎢 💈 🎪 🚂 🚃 🚄 🚅 🚆 🚇 🚈 🚉 🚊 🚝 🚞 🚋 🚌 🚍 🚎 🚐 🚑 🚒 🚓 🚔 🚕 🚖 🚗 🚘 🚙 🛻 🚚 🚛 🚜 🏎️ 🏍️ 🛵 🦽 🦼 🛺 🚲 🛴 🛹 🛼 🚏 🛣️ 🛤️ 🛢️ ⛽ 🚨 🚥 🚦 🛑 🚧 ⚓ ⛵ 🛶 🚤 🛳️ ⛴️ 🛥️ 🚢 ✈️ 🛩️ 🛫 🛬 🪂 💺 🚁 🚟 🚠 🚡 🛰️ 🚀 🛸 🛎️ 🧳 ⌛ ⏳ ⌚ ⏰ ⏱️ ⏲️ 🕰️ 🌡️ 🗿
    `),
  },
  {
    key: 'activities', label: 'Activities', icon: '⚽', emojis: split(`
    🎃 🎄 🎆 🎇 🧨 ✨ 🎈 🎉 🎊 🎋 🎍 🎎 🎏 🎐 🎑 🧧 🎀 🎁 🎗️ 🎟️ 🎫 🎖️ 🏆 🏅 🥇 🥈 🥉 ⚽ ⚾ 🥎 🏀 🏐 🏈 🏉 🎾 🥏 🎳 🏏 🏑 🏒 🥍 🏓 🏸 🥊 🥋 🥅 ⛳ ⛸️ 🎣 🤿 🎽 🎿 🛷 🥌 🎯 🪀 🪁 🔫 🎱 🔮 🪄 🎮 🕹️ 🎰 🎲 🧩 🧸 🪅 🪆 ♠️ ♥️ ♦️ ♣️ ♟️ 🃏 🀄 🎴 🎭 🖼️ 🎨 🧵 🪡 🧶 🪢 🎤 🎧 🎼 🎹 🥁 🪘 🎷 🎺 🪗 🎸 🪕 🎻 🎬 🏹
    `),
  },
  {
    key: 'objects', label: 'Objects', icon: '💡', emojis: split(`
    ⌚ 📱 📲 💻 ⌨️ 🖥️ 🖨️ 🖱️ 🖲️ 🕹️ 🗜️ 💽 💾 💿 📀 📼 📷 📸 📹 🎥 📽️ 🎞️ 📞 ☎️ 📟 📠 📺 📻 🎙️ 🎚️ 🎛️ 🧭 ⏱️ ⏲️ ⏰ 🕰️ ⌛ ⏳ 📡 🔋 🪫 🔌 💡 🔦 🕯️ 🪔 🧯 🛢️ 💸 💵 💴 💶 💷 🪙 💰 💳 🧾 💎 ⚖️ 🪜 🧰 🪛 🔧 🔨 ⚒️ 🛠️ ⛏️ 🪚 🔩 ⚙️ 🪤 🧱 ⛓️ 🧲 🔫 💣 🧨 🪓 🔪 🗡️ ⚔️ 🛡️ 🚬 ⚰️ 🪦 ⚱️ 🏺 🔮 📿 🧿 🪬 💈 ⚗️ 🔭 🔬 🕳️ 🩹 🩺 🩻 💊 💉 🩸 🧬 🦠 🧫 🧪 🌡️ 🧹 🪠 🧺 🧻 🚽 🚰 🚿 🛁 🛀 🧼 🪥 🪒 🧽 🪣 🧴 🛎️ 🔑 🗝️ 🚪 🪑 🛋️ 🛏️ 🛌 🧸 🪆 🖼️ 🪞 🪟 🛍️ 🛒 🎁 🎈 🎏 🎀 🪄 🪅 🎊 🎉 🎎 🏮 🎐 🧧 ✉️ 📩 📨 📧 💌 📥 📤 📦 🏷️ 🪧 📪 📫 📬 📭 📮 📯 📜 📃 📄 📑 🧾 📊 📈 📉 🗒️ 🗓️ 📆 📅 🗑️ 📇 🗃️ 🗳️ 🗄️ 📋 📁 📂 🗂️ 🗞️ 📰 📓 📔 📒 📕 📗 📘 📙 📚 📖 🔖 🧷 🔗 📎 🖇️ 📐 📏 🧮 📌 📍 ✂️ 🖊️ 🖋️ ✒️ 🖌️ 🖍️ 📝 ✏️ 🔍 🔎 🔏 🔐 🔒 🔓
    `),
  },
  {
    key: 'symbols', label: 'Symbols', icon: '❤️', emojis: split(`
    ❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❤️‍🔥 ❤️‍🩹 ❣️ 💕 💞 💓 💗 💖 💘 💝 💟 ☮️ ✝️ ☪️ 🕉️ ☸️ ✡️ 🔯 🕎 ☯️ ☦️ 🛐 ⛎ ♈ ♉ ♊ ♋ ♌ ♍ ♎ ♏ ♐ ♑ ♒ ♓ 🆔 ⚛️ 🉑 ☢️ ☣️ 📴 📳 🈶 🈚 🈸 🈺 🈷️ ✴️ 🆚 💮 🉐 ㊙️ ㊗️ 🈴 🈵 🈹 🈲 🅰️ 🅱️ 🆎 🆑 🅾️ 🆘 ❌ ⭕ 🛑 ⛔ 📛 🚫 💯 💢 ♨️ 🚷 🚯 🚳 🚱 🔞 📵 🚭 ❗ ❕ ❓ ❔ ‼️ ⁉️ 🔅 🔆 〽️ ⚠️ 🚸 🔱 ⚜️ 🔰 ♻️ ✅ 🈯 💹 ❇️ ✳️ ❎ 🌐 💠 Ⓜ️ 🌀 💤 🏧 🚾 ♿ 🅿️ 🛗 🈳 🈂️ 🛂 🛃 🛄 🛅 🚹 🚺 🚼 ⚧️ 🚻 🚮 🎦 📶 🈁 🔣 ℹ️ 🔤 🔡 🔠 🆖 🆗 🆙 🆒 🆕 🆓 0️⃣ 1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣ 6️⃣ 7️⃣ 8️⃣ 9️⃣ 🔟 🔢 #️⃣ *️⃣ ⏏️ ▶️ ⏸️ ⏯️ ⏹️ ⏺️ ⏭️ ⏮️ ⏩ ⏪ ⏫ ⏬ ◀️ 🔼 🔽 ➡️ ⬅️ ⬆️ ⬇️ ↗️ ↘️ ↙️ ↖️ ↕️ ↔️ ↪️ ↩️ ⤴️ ⤵️ 🔀 🔁 🔂 🔄 🔃 🎵 🎶 ➕ ➖ ➗ ✖️ 🟰 ♾️ 💲 💱 ™️ ©️ ®️ 〰️ ➰ ➿ 🔚 🔙 🔛 🔝 🔜 ✔️ ☑️ 🔘 🔴 🟠 🟡 🟢 🔵 🟣 ⚫ ⚪ 🟤 🔺 🔻 🔸 🔹 🔶 🔷 🔳 🔲 ▪️ ▫️ ◾ ◽ ◼️ ◻️ 🟥 🟧 🟨 🟩 🟦 🟪 ⬛ ⬜ 🟫 🔈 🔇 🔉 🔊 🔔 🔕 📣 📢 👁️‍🗨️ 💬 💭 🗯️ ♠️ ♣️ ♥️ ♦️ 🕐 🕑 🕒 🕓 🕔 🕕 🕖 🕗 🕘 🕙 🕚 🕛
    `),
  },
  {
    key: 'flags', label: 'Flags', icon: '🏁', emojis: split(`
    🏁 🚩 🎌 🏴 🏳️ 🏳️‍🌈 🏳️‍⚧️ 🏴‍☠️
    `),
  },
];

// Compact search index: keyword → emoji, covering the terms people actually type.
// (Not every emoji is indexed; browsing by category covers the rest.)
const KEYWORDS: Record<string, string> = {
  smile: '😀', happy: '😀', grin: '😁', laugh: '😂', joy: '😂', rofl: '🤣', lol: '😂',
  wink: '😉', blush: '😊', love: '😍', hearteyes: '😍', kiss: '😘', yum: '😋',
  tongue: '😛', crazy: '🤪', money: '🤑', hug: '🤗', think: '🤔', thinking: '🤔',
  quiet: '🤫', shush: '🤫', neutral: '😐', roll: '🙄', eyeroll: '🙄', smirk: '😏',
  unamused: '😒', relieved: '😌', pensive: '😔', sleepy: '😪', sleep: '😴', tired: '😫',
  mask: '😷', sick: '🤢', vomit: '🤮', sneeze: '🤧', hot: '🥵', cold: '🥶', dizzy: '😵',
  mindblown: '🤯', explode: '🤯', cowboy: '🤠', party: '🥳', celebrate: '🥳', cool: '😎',
  nerd: '🤓', geek: '🤓', worried: '😟', frown: '🙁', open: '😮', shock: '😲', flushed: '😳',
  pleading: '🥺', cry: '😢', sob: '😭', tears: '😭', scream: '😱', fear: '😨', anxious: '😰',
  sweat: '😓', yawn: '🥱', triumph: '😤', angry: '😠', mad: '😡', rage: '🤬', swear: '🤬',
  devil: '😈', imp: '👿', skull: '💀', death: '💀', dead: '💀', poop: '💩', poo: '💩',
  clown: '🤡', ogre: '👹', ghost: '👻', boo: '👻', alien: '👽', ufo: '🛸', monster: '👾',
  robot: '🤖', bot: '🤖', cat: '🐱', catface: '😺', heart: '❤️', redheart: '❤️',
  orangeheart: '🧡', yellowheart: '💛', greenheart: '💚', blueheart: '💙', purpleheart: '💜',
  blackheart: '🖤', whiteheart: '🤍', brokenheart: '💔', sparklingheart: '💖', gift: '🎁',
  hundred: '💯', hunderd: '💯', anger: '💢', boom: '💥', collision: '💥', sweatdrops: '💦',
  dash: '💨', hole: '🕳️', speech: '💬', thought: '💭', zzz: '💤',
  wave: '👋', hi: '👋', bye: '👋', hand: '✋', stop: '✋', raised: '✋', vulcan: '🖖',
  ok: '👌', okhand: '👌', pinch: '🤏', victory: '✌️', peace: '✌️', fingerscrossed: '🤞',
  luck: '🤞', loveyou: '🤟', rock: '🤘', horns: '🤘', callme: '🤙', point: '👆',
  up: '👆', down: '👇', left: '👈', right: '👉', middlefinger: '🖕', fu: '🖕',
  thumbsup: '👍', thumbup: '👍', yes: '👍', like: '👍', approve: '👍', plus1: '👍',
  thumbsdown: '👎', thumbdown: '👎', no: '👎', dislike: '👎', fist: '✊', punch: '👊',
  clap: '👏', applause: '👏', raisehands: '🙌', hooray: '🙌', openhands: '👐',
  handshake: '🤝', deal: '🤝', pray: '🙏', please: '🙏', thanks: '🙏', thankyou: '🙏',
  nails: '💅', selfie: '🤳', muscle: '💪', strong: '💪', flex: '💪', ear: '👂',
  nose: '👃', brain: '🧠', tooth: '🦷', eyes: '👀', look: '👀', eye: '👁️', tongueout: '👅',
  lips: '👄', baby: '👶', child: '🧒', boy: '👦', girl: '👧', man: '👨', woman: '👩',
  old: '👴', grandpa: '👴', grandma: '👵', shrug: '🤷', facepalm: '🤦', cop: '👮',
  police: '👮', detective: '🕵️', ninja: '🥷', worker: '👷', prince: '🤴', princess: '👸',
  king: '🫅', queen: '🫅', angel: '👼', santa: '🎅', hero: '🦸', superhero: '🦸',
  villain: '🦹', wizard: '🧙', mage: '🧙', fairy: '🧚', vampire: '🧛', mermaid: '🧜',
  elf: '🧝', genie: '🧞', zombie: '🧟', walk: '🚶', run: '🏃', dance: '💃', dancer: '💃',
  dog: '🐶', puppy: '🐶', wolf: '🐺', fox: '🦊', lion: '🦁', tiger: '🐯', horse: '🐴',
  unicorn: '🦄', cow: '🐮', pig: '🐷', frog: '🐸', monkey: '🐵', chicken: '🐔',
  penguin: '🐧', bird: '🐦', eagle: '🦅', duck: '🦆', owl: '🦉', bat: '🦇', bear: '🐻',
  koala: '🐨', panda: '🐼', snake: '🐍', dragon: '🐉', turtle: '🐢', fish: '🐟',
  dolphin: '🐬', whale: '🐳', octopus: '🐙', snail: '🐌', butterfly: '🦋', bug: '🐛',
  ant: '🐜', bee: '🐝', ladybug: '🐞', spider: '🕷️', flower: '🌸', rose: '🌹',
  sunflower: '🌻', tulip: '🌷', plant: '🌱', tree: '🌳', palm: '🌴', cactus: '🌵',
  clover: '🍀', shamrock: '☘️', mushroom: '🍄', earth: '🌍', globe: '🌍', moon: '🌙',
  star: '⭐', star2: '🌟', sun: '☀️', cloud: '☁️', rainbow: '🌈', umbrella: '☔',
  lightning: '⚡', zap: '⚡', snow: '❄️', snowflake: '❄️', snowman: '⛄', fire: '🔥',
  lit: '🔥', flame: '🔥', water: '💧', droplet: '💧', wave2: '🌊', ocean: '🌊',
  apple: '🍎', banana: '🍌', grapes: '🍇', watermelon: '🍉', orange: '🍊', lemon: '🍋',
  strawberry: '🍓', cherry: '🍒', peach: '🍑', pineapple: '🍍', avocado: '🥑',
  eggplant: '🍆', corn: '🌽', pepper: '🌶️', bread: '🍞', cheese: '🧀', meat: '🍖',
  bacon: '🥓', burger: '🍔', fries: '🍟', pizza: '🍕', hotdog: '🌭', taco: '🌮',
  burrito: '🌯', egg: '🥚', salad: '🥗', popcorn: '🍿', rice: '🍚', ramen: '🍜',
  noodles: '🍜', spaghetti: '🍝', sushi: '🍣', shrimp: '🍤', icecream: '🍨',
  doughnut: '🍩', donut: '🍩', cookie: '🍪', cake: '🎂', birthday: '🎂', cupcake: '🧁',
  pie: '🥧', chocolate: '🍫', candy: '🍬', lollipop: '🍭', honey: '🍯', milk: '🥛',
  coffee: '☕', tea: '🍵', sake: '🍶', champagne: '🍾', wine: '🍷', cocktail: '🍸',
  tropical: '🍹', beer: '🍺', beers: '🍻', cheers: '🥂', whiskey: '🥃', cup: '🥤',
  soda: '🥤', bubbletea: '🧋', boba: '🧋', ice: '🧊', house: '🏠', home: '🏠',
  office: '🏢', building: '🏢', hospital: '🏥', bank: '🏦', hotel: '🏨', school: '🏫',
  factory: '🏭', castle: '🏰', church: '⛪', mosque: '🕌', tent: '⛺', fountain: '⛲',
  city: '🏙️', sunrise: '🌅', sunset: '🌇', bridge: '🌉', train: '🚆', metro: '🚇',
  bus: '🚌', ambulance: '🚑', firetruck: '🚒', taxi: '🚕', car: '🚗', truck: '🚚',
  tractor: '🚜', racecar: '🏎️', motorcycle: '🏍️', scooter: '🛵', bike: '🚲',
  bicycle: '🚲', skateboard: '🛹', anchor: '⚓', sailboat: '⛵', boat: '⛵',
  speedboat: '🚤', ship: '🚢', airplane: '✈️', plane: '✈️', takeoff: '🛫',
  parachute: '🪂', helicopter: '🚁', rocket: '🚀', launch: '🚀', satellite: '🛰️',
  luggage: '🧳', hourglass: '⏳', watch: '⌚', alarm: '⏰', clock: '🕰️', hot2: '🌡️',
  jackolantern: '🎃', pumpkin: '🎃', christmas: '🎄', christmastree: '🎄',
  fireworks: '🎆', sparkler: '🎇', firecracker: '🧨', sparkles: '✨', shiny: '✨',
  balloon: '🎈', tada: '🎉', confetti: '🎉', partypopper: '🎉', celebration: '🎊',
  ribbon: '🎀', trophy: '🏆', win: '🏆', medal: '🏅', gold: '🥇', silver: '🥈',
  bronze: '🥉', soccer: '⚽', football: '🏈', baseball: '⚾', basketball: '🏀',
  volleyball: '🏐', tennis: '🎾', bowling: '🎳', pingpong: '🏓', badminton: '🏸',
  boxing: '🥊', golf: '⛳', fishing: '🎣', ski: '🎿', target: '🎯', bullseye: '🎯',
  dart: '🎯', gamecontroller: '🎮', game: '🎮', gaming: '🎮', joystick: '🕹️',
  slot: '🎰', dice: '🎲', puzzle: '🧩', teddy: '🧸', spades: '♠️', hearts: '♥️',
  diamonds: '♦️', clubs: '♣️', joker: '🃏', art: '🎨', paint: '🎨', palette: '🎨',
  thread: '🧵', yarn: '🧶', mic: '🎤', microphone: '🎤', headphones: '🎧', music: '🎵',
  notes: '🎶', piano: '🎹', drum: '🥁', saxophone: '🎷', trumpet: '🎺', guitar: '🎸',
  violin: '🎻', clapper: '🎬', movie: '🎬', film: '🎬', bow: '🏹', archery: '🏹',
  phone: '📱', mobile: '📱', iphone: '📱', laptop: '💻', computer: '💻', keyboard: '⌨️',
  desktop: '🖥️', printer: '🖨️', camera: '📷', video: '📹', videocamera: '📹',
  movie2: '🎥', telephone: '☎️', pager: '📟', fax: '📠', tv: '📺', television: '📺',
  radio: '📻', battery: '🔋', plug: '🔌', bulb: '💡', idea: '💡', flashlight: '🔦',
  candle: '🕯️', fireextinguisher: '🧯', cash: '💵', dollar: '💵', euro: '💶',
  moneybag: '💰', creditcard: '💳', receipt: '🧾', gem: '💎', diamond: '💎',
  scale: '⚖️', ladder: '🪜', toolbox: '🧰', screwdriver: '🪛', wrench: '🔧', hammer: '🔨',
  tools: '🛠️', pick: '⛏️', saw: '🪚', nut: '🔩', gear: '⚙️', settings: '⚙️',
  magnet: '🧲', bomb: '💣', axe: '🪓', knife: '🔪', dagger: '🗡️', sword: '⚔️',
  swords: '⚔️', shield: '🛡️', coffin: '⚰️', urn: '⚱️', crystal: '🔮', crystalball: '🔮',
  beads: '📿', nazar: '🧿', evileye: '🧿', telescope: '🔭', microscope: '🔬',
  bandage: '🩹', stethoscope: '🩺', pill: '💊', medicine: '💊', syringe: '💉',
  vaccine: '💉', blood: '🩸', dna: '🧬', microbe: '🦠', germ: '🦠', testtube: '🧪',
  broom: '🧹', plunger: '🪠', basket: '🧺', toiletpaper: '🧻', toilet: '🚽',
  shower: '🚿', bath: '🛁', bathtub: '🛁', soap: '🧼', razor: '🪒', sponge: '🧽',
  bucket: '🪣', lotion: '🧴', bell: '🛎️', key: '🔑', oldkey: '🗝️', door: '🚪',
  chair: '🪑', couch: '🛋️', sofa: '🛋️', bed: '🛏️', mirror: '🪞', window: '🪟',
  shopping: '🛍️', cart: '🛒', shoppingcart: '🛒', mail: '✉️', envelope: '✉️',
  email: '📧', inbox: '📥', outbox: '📤', package: '📦', parcel: '📦', label: '🏷️',
  tag: '🏷️', scroll: '📜', page: '📄', document: '📄', chart: '📊', graph: '📈',
  increasing: '📈', decreasing: '📉', calendar: '📆', date: '📅', clipboard: '📋',
  folder: '📁', file: '📁', news: '📰', newspaper: '📰', notebook: '📓', book: '📖',
  books: '📚', bookmark: '🔖', link: '🔗', paperclip: '📎', ruler: '📐', abacus: '🧮',
  pushpin: '📌', pin: '📌', roundpushpin: '📍', location: '📍', scissors: '✂️',
  pen: '🖊️', fountainpen: '🖋️', brush: '🖌️', crayon: '🖍️', memo: '📝', note: '📝',
  writing: '📝', pencil: '✏️', edit: '✏️', search: '🔍', magnify: '🔍', zoom: '🔍',
  lock: '🔒', locked: '🔒', unlock: '🔓', unlocked: '🔓', secure: '🔒',
  cross: '✝️', peacesign: '☮️', yinyang: '☯️', aries: '♈', taurus: '♉', gemini: '♊',
  cancer: '♋', leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏', sagittarius: '♐',
  capricorn: '♑', aquarius: '♒', pisces: '♓', radioactive: '☢️', biohazard: '☣️',
  x: '❌', cross2: '❌', wrong: '❌', circle: '⭕', stopsign: '🛑', noentry: '⛔',
  nogo: '🚫', prohibited: '🚫', warning: '⚠️', caution: '⚠️', trident: '🔱',
  recycle: '♻️', check: '✅', checkmark: '✅', done: '✅', complete: '✅', correct: '✅',
  question: '❓', exclamation: '❗', bang: '❗', doubleexclamation: '‼️',
  interrobang: '⁉️', part: '〽️', new: '🆕', free: '🆓', up2: '🆙', cool2: '🆒',
  ok2: '🆗', sos: '🆘', vs: '🆚', abc: '🔤', abcd: '🔡', capitalabcd: '🔠',
  play: '▶️', pause: '⏸️', stopbutton: '⏹️', record: '⏺️', next: '⏭️', previous: '⏮️',
  fastforward: '⏩', rewind: '⏪', arrow: '➡️', arrowright: '➡️', arrowleft: '⬅️',
  arrowup: '⬆️', arrowdown: '⬇️', shuffle: '🔀', repeat: '🔁', loop: '🔁',
  plus: '➕', add: '➕', minus: '➖', divide: '➗', multiply: '✖️', infinity: '♾️',
  currency: '💱', tm: '™️', copyright: '©️', registered: '®️', end: '🔚', back: '🔙',
  top: '🔝', soon: '🔜', ballotcheck: '☑️', radiobutton: '🔘', redcircle: '🔴',
  orangecircle: '🟠', yellowcircle: '🟡', greencircle: '🟢', bluecircle: '🔵',
  purplecircle: '🟣', blackcircle: '⚫', whitecircle: '⚪', triangle: '🔺',
  redsquare: '🟥', greensquare: '🟩', bluesquare: '🟦', mute: '🔇', speaker: '🔊',
  loud: '🔊', notification: '🔔', bell2: '🔔', silent: '🔕', mega: '📣', megaphone: '📣',
  loudspeaker: '📢', announce: '📢', speech2: '💬', chat: '💬', comment: '💬',
  flag: '🚩', redflag: '🚩', checkeredflag: '🏁', finish: '🏁', racing: '🏁',
  pride: '🏳️‍🌈', rainbowflag: '🏳️‍🌈', trans: '🏳️‍⚧️', pirate: '🏴‍☠️', blackflag: '🏴', whiteflag: '🏳️',
};

const ALL_EMOJI = EMOJI_CATEGORIES.flatMap((c) => c.emojis);
const RECENTS_KEY = 'fxmchat_emoji_recent';

function loadRecents(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]').slice(0, 24); } catch { return []; }
}
export function pushRecentEmoji(e: string): void {
  try {
    const cur = loadRecents().filter((x) => x !== e);
    localStorage.setItem(RECENTS_KEY, JSON.stringify([e, ...cur].slice(0, 24)));
  } catch { /* ignore */ }
}

function searchEmoji(q: string): string[] {
  const s = q.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!s) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const [kw, e] of Object.entries(KEYWORDS)) {
    if (kw.startsWith(s) && !seen.has(e)) { seen.add(e); out.push(e); }
  }
  for (const [kw, e] of Object.entries(KEYWORDS)) {
    if (!kw.startsWith(s) && kw.includes(s) && !seen.has(e)) { seen.add(e); out.push(e); }
  }
  return out;
}

export function EmojiPicker({ onPick, className = '' }: { onPick: (e: string) => void; className?: string }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState(EMOJI_CATEGORIES[0].key);
  const scrollRef = useRef<HTMLDivElement>(null);
  const recents = useMemo(() => loadRecents(), []);
  const results = useMemo(() => (q ? searchEmoji(q) : []), [q]);

  const pick = (e: string) => { pushRecentEmoji(e); onPick(e); };
  const jump = (key: string) => {
    setCat(key);
    scrollRef.current?.querySelector(`[data-cat="${key}"]`)?.scrollIntoView({ block: 'start' });
  };

  return (
    <div className={`fx-emoji ${className}`} onClick={(e) => e.stopPropagation()}>
      <input
        className="fx-emoji-search"
        placeholder="Search emoji"
        value={q}
        autoFocus
        onChange={(e) => setQ(e.target.value)}
      />
      {!q && (
        <div className="fx-emoji-tabs">
          {EMOJI_CATEGORIES.map((c) => (
            <button
              type="button"
              key={c.key}
              className={`fx-emoji-tab${cat === c.key ? ' is-active' : ''}`}
              title={c.label}
              onClick={() => jump(c.key)}
            >
              {c.icon}
            </button>
          ))}
        </div>
      )}
      <div className="fx-emoji-scroll" ref={scrollRef}>
        {q ? (
          results.length ? (
            <div className="fx-emoji-grid">
              {results.map((e, i) => (
                <button type="button" key={`${e}-${i}`} className="fx-emoji-btn" onClick={() => pick(e)}>{e}</button>
              ))}
            </div>
          ) : (
            <div className="fx-emoji-empty">No emoji for “{q}”</div>
          )
        ) : (
          <>
            {recents.length > 0 && (
              <div className="fx-emoji-sec">
                <div className="fx-emoji-h">Recent</div>
                <div className="fx-emoji-grid">
                  {recents.map((e, i) => (
                    <button type="button" key={`r-${e}-${i}`} className="fx-emoji-btn" onClick={() => pick(e)}>{e}</button>
                  ))}
                </div>
              </div>
            )}
            {EMOJI_CATEGORIES.map((c) => (
              <div className="fx-emoji-sec" data-cat={c.key} key={c.key}>
                <div className="fx-emoji-h">{c.label}</div>
                <div className="fx-emoji-grid">
                  {c.emojis.map((e, i) => (
                    <button type="button" key={`${c.key}-${e}-${i}`} className="fx-emoji-btn" onClick={() => pick(e)}>{e}</button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export { ALL_EMOJI };
